import React, { useContext, useEffect, useState } from 'react'
import { IAnalyticsContext, IAnalyticsData } from '../@types/analytics'
import { IProviderProps } from '../@types/context'
import { IEnv } from '../@types/env'

declare global {
  interface Window {
    digitalData: IAnalyticsData
  }
}

declare global {
  const _satellite: {
    track: (value: string) => void
    pageBottom: () => void
  }
}

export const AnalyticsContext: React.Context<IAnalyticsContext> =
  React.createContext<IAnalyticsContext>({
    setPage: () => null,
  })

// eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
const digitalData: IAnalyticsData = window.digitalData || {
  pageInfo: {},
  internalCampaignInfo: {},
  vehicleInfo: {},
}

export const AnalyticsProvider: React.FC<IProviderProps> = ({ children }) => {
  const [page, setPage] = useState<string>('landing')

  useEffect(() => {
    if (process.env.REACT_APP_TRACKING !== 'true') return
    updateDigitalData()
  }, [page])

  const updateDigitalData = (): void => {
    const { REACT_APP_CAR_TYPE, REACT_APP_COUNTRY, REACT_APP_LANGUAGE } =
      process.env as unknown as IEnv
    const country = REACT_APP_COUNTRY
    const language = REACT_APP_LANGUAGE
    const carType = REACT_APP_CAR_TYPE
    const { innerWidth, innerHeight } = window

    digitalData.vehicleInfo.vehicleType = 'car'
    digitalData.vehicleInfo.modelName = 'chevrolet'
    digitalData.vehicleInfo.modelYear = '2023'
    digitalData.vehicleInfo.bodyStyle = `${carType}`

    if (country === 'ca') {
      if (language === 'fr') {
        digitalData.pageInfo.url = ''
      } else {
        digitalData.pageInfo.url = ''
      }
    } else {
      digitalData.pageInfo.url = ''
    }
    digitalData.pageInfo.languageSelected = `${
      language === 'fr' ? 'French' : 'English'
    }`
    digitalData.pageInfo.siteName = `quantum_${
      country === 'ca' ? 'Canada' : 'United States'
    }`
    digitalData.pageInfo.brand = 'chevrolet'
    digitalData.pageInfo.country = `${
      country === 'ca' ? 'Canada' : 'United States'
    }`
    digitalData.pageInfo.region = 'North America'

    digitalData.pageInfo.siteSectionsLevel1 = 'vehicles'
    digitalData.pageInfo.siteSectionsLevel2 = `${
      country === 'ca' ? '2023-chevrolet' : '2023'
    }`
    digitalData.pageInfo.siteSectionsLevel3 = `${
      country === 'ca' ? 'undefined' : carType
    }`
    digitalData.pageInfo.siteSectionsLevel4 = ''
    digitalData.pageInfo.pageType = `${
      country === 'ca' ? 'performance' : 'performance'
    }`
    const siteSections = [
      digitalData.pageInfo.siteSectionsLevel1,
      digitalData.pageInfo.siteSectionsLevel2,
      digitalData.pageInfo.siteSectionsLevel3,
    ]
    if (country === 'us') {
      siteSections.push(digitalData.pageInfo.siteSectionsLevel4)
      digitalData.pageInfo.seoStrategyPageName = `chevrolet - ${carType}`
      digitalData.pageInfo.pageSubType = ''
      digitalData.pageInfo.pageMarketing = ''
      digitalData.pageInfo.pageMarketingSubType = ''
      digitalData.vehicleInfo.familyOfVehicles = ''
      digitalData.vehicleInfo.trimLevel = ''
      let renderedExperience = 'small'
      if (innerWidth >= 640 && innerWidth < 1024) renderedExperience = 'medium'
      if (innerWidth >= 1024) renderedExperience = 'large'
      digitalData.pageInfo.renderedExperience = renderedExperience
      digitalData.pageInfo.viewport = `${innerWidth}x${innerHeight}`
      if (innerWidth > innerHeight) {
        digitalData.pageInfo.orientation = 'landscape'
      } else {
        digitalData.pageInfo.orientation = 'portrait'
      }
    }
    digitalData.pageInfo.siteSectionsLevel5 = `${
      country === 'ca'
        ? `${siteSections.join(':')}`
        : `${siteSections.join(':')}:${page}`
    }`
    digitalData.pageInfo.pageName = `${
      country === 'ca'
        ? `ch:NA:CA:${language}:t1:${digitalData.pageInfo.siteSectionsLevel5}`
        : `ch:na:${country}:${language}:t1:${digitalData.pageInfo.siteSectionsLevel5}`
    }`

    _satellite.track('page-view')
  }

  return (
    <AnalyticsContext.Provider
      value={{
        setPage,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  )
}

export const useAnalytics = (): IAnalyticsContext =>
  useContext(AnalyticsContext)
